import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SegmentedToggle } from "../../../components/common/SegmentedToggle";
import { SimpleDropdown } from "../../../components/common/SimpleDropdown";
import { CARD } from "../../dashboard/profile/profileCard";
import { fetchFollowedTeams, followTeam, unfollowTeam } from "../../../features/coach/coachProfileSlice";
import teamService from "../../../services/teamService";

// Teams landing page: a searchable directory of every NJCAA and 3C2A program.
// All teams load in one go (~660 rows, a few hundred are opponents like summer
// or 4-year teams that we leave out) and are filtered in the browser. Filters
// live in the URL so "Back to Teams" from a profile returns to the same view.

const PAGE_SIZE = 24;
const FETCH_LIMIT = 500;

const NJCAA_DIVISIONS = { I: "D1", II: "D2", III: "D3" };
const CCCAA_DIVISIONS = { "Northern California": "North", "Southern California": "South" };

const DIVISION_OPTIONS = {
  NJCAA: [
    { value: "D1", label: "Division I" },
    { value: "D2", label: "Division II" },
    { value: "D3", label: "Division III" },
  ],
  "3C2A": [
    { value: "North", label: "Northern California" },
    { value: "South", label: "Southern California" },
  ],
};

const ASSOCIATIONS = [
  { value: "", label: "All" },
  { value: "NJCAA", label: "NJCAA" },
  { value: "3C2A", label: "3C2A" },
];

const SORTS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "record", label: "Best record" },
  { value: "state", label: "State" },
];

const FILTER_KEYS = ["q", "assoc", "div", "region", "state", "following"];

// Turns a raw team row into what the directory shows, or null if it isn't an
// NJCAA or 3C2A program (summer, high-school and 4-year opponents).
const toProgram = (team) => {
  const league = (team.league || "").toUpperCase();
  let association;
  let division;
  if (league.startsWith("NJCAA")) {
    association = "NJCAA";
    division = NJCAA_DIVISIONS[team.division];
  } else if (league.startsWith("3C2A")) {
    association = "3C2A";
    division = CCCAA_DIVISIONS[team.division];
  }
  if (!division) return null;

  const wins = Number(team.wins) || 0;
  const losses = Number(team.losses) || 0;
  const ties = Number(team.ties) || 0;
  const games = wins + losses + ties;
  const record = team.record && team.record !== "0-0" ? team.record : games ? `${wins}-${losses}${ties ? `-${ties}` : ""}` : null;
  const conferenceShort = team.conference?.match(/\(([^)]+)\)/)?.[1] || team.conference || null;

  return {
    team,
    id: team._id,
    name: team.name,
    logo: team.logo,
    association,
    division,
    divisionLabel: association === "NJCAA" ? `NJCAA ${division}` : `3C2A ${division}`,
    region: association === "NJCAA" && team.region ? String(team.region) : null,
    state: team.state || (association === "3C2A" ? "California" : null),
    town: team.town || null,
    conference: team.conference || null,
    conferenceShort,
    record,
    winPct: games ? (wins + ties / 2) / games : -1,
    searchText: [team.name, team.town, team.state, team.conference].filter(Boolean).join(" ").toLowerCase(),
  };
};

// Loaded once per visit; going back and forth to team profiles reuses it.
let programCache = null;

const loadPrograms = async () => {
  if (programCache) return programCache;
  const teams = [];
  for (let page = 1; ; page += 1) {
    const data = await teamService.getCoachTeams({ page, limit: FETCH_LIMIT });
    teams.push(...(data?.teams || []));
    if (page >= (data?.pagination?.totalPages || 1)) break;
  }
  programCache = teams.map(toProgram).filter(Boolean);
  return programCache;
};

const DIVISION_BADGE = {
  D1: "bg-jp-accent/15 text-jp-accent-ink",
  D2: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  D3: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  North: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  South: "bg-rose-500/15 text-rose-700 dark:text-rose-400",
};

const ProgramLogo = ({ name, logo }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="w-14 h-14 flex-shrink-0 rounded-full bg-white border border-jp-outline/60 p-2 flex items-center justify-center overflow-hidden shadow-sm">
      {logo && !failed ? (
        <img src={logo} alt="" loading="lazy" onError={() => setFailed(true)} className="w-full h-full object-contain" />
      ) : (
        <span className="text-jp-band font-extrabold text-sm">{name?.charAt(0) || "?"}</span>
      )}
    </span>
  );
};

const STAR_PATH = "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

// Navy outline star; filled yellow once the coach follows the team.
const FollowStar = ({ name, following, busy, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={busy}
    aria-pressed={following}
    aria-label={following ? `Unfollow ${name}` : `Follow ${name}`}
    title={following ? "Unfollow team" : "Follow team"}
    className="absolute top-3 right-3 z-10 p-1.5 rounded-full hover:bg-jp-section transition-colors disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
  >
    <svg
      className={`w-5 h-5 transition-colors ${following ? "text-yellow-400" : "text-jp-band dark:text-slate-300"}`}
      viewBox="0 0 24 24"
      fill={following ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={STAR_PATH} />
    </svg>
  </button>
);

// The star is a separate button laid over the card (buttons can't nest).
const ProgramCard = ({ program, following, followBusy, onOpen, onToggleFollow }) => (
  <div className="relative flex">
    <FollowStar name={program.name} following={following} busy={followBusy} onToggle={() => onToggleFollow(program)} />
    <button
      type="button"
      onClick={() => onOpen(program)}
      className="group flex-1 flex flex-col text-left rounded-2xl bg-jp-card border border-jp-outline/70 shadow-sm p-4 hover:border-jp-accent/60 hover:shadow-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
    >
      <span className="flex items-start gap-3 pr-8">
        <ProgramLogo name={program.name} logo={program.logo} />
        <span className="min-w-0 flex-1">
          <span className="block text-jp-ink font-extrabold leading-snug group-hover:text-jp-accent-ink transition-colors">
            {program.name}
          </span>
          <span className="block text-jp-muted text-xs mt-0.5 truncate">
            {[program.town, program.state].filter(Boolean).join(", ") || "Location not listed"}
          </span>
        </span>
      </span>

      <span className="mt-3 mb-3 flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
        <span className={`px-2 py-0.5 rounded-full ${DIVISION_BADGE[program.division]}`}>{program.divisionLabel}</span>
        {program.region && <span className="px-2 py-0.5 rounded-full bg-jp-section text-jp-muted">Region {program.region}</span>}
        {program.conferenceShort && (
          <span title={program.conference} className="px-2 py-0.5 rounded-full bg-jp-section text-jp-muted max-w-[10rem] truncate">
            {program.conferenceShort}
          </span>
        )}
      </span>

      <span className="mt-auto pt-3 flex items-center justify-between border-t border-jp-outline/40">
        <span className="text-xs text-jp-muted">
          Record{" "}
          <span className="text-jp-ink font-extrabold tabular-nums">{program.record || "–"}</span>
        </span>
        <span className="text-xs font-bold text-jp-accent-ink inline-flex items-center gap-1">
          View team
          <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </span>
    </button>
  </div>
);

const SkeletonCard = () => (
  <div className="rounded-2xl bg-jp-card border border-jp-outline/70 p-4 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 rounded-full bg-jp-section" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-jp-section rounded w-3/4" />
        <div className="h-3 bg-jp-section rounded w-1/2" />
      </div>
    </div>
    <div className="mt-4 h-4 bg-jp-section rounded w-2/3" />
    <div className="mt-4 h-3 bg-jp-section rounded w-1/3" />
  </div>
);

const FilterLabel = ({ children }) => (
  <span className="block text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1.5">{children}</span>
);

export const TeamDirectory = ({ basePath = "/coach" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const { followedTeams } = useSelector((state) => state.coachProfile);
  const [state, setState] = useState({ loading: !programCache, error: null, programs: programCache || [] });
  const [visible, setVisible] = useState(PAGE_SIZE);

  const query = params.get("q") || "";
  const association = params.get("assoc") || "";
  const division = params.get("div") || "";
  const region = params.get("region") || "";
  const stateFilter = params.get("state") || "";
  const followingOnly = params.get("following") === "1";
  const sort = params.get("sort") || "name";

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    loadPrograms()
      .then((programs) => setState({ loading: false, error: null, programs }))
      .catch((error) =>
        setState({ loading: false, error: error.response?.data?.message || "Couldn't load teams.", programs: [] })
      );
  }, []);

  useEffect(() => {
    load();
    dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
  }, [load, dispatch]);

  // Updates URL filters; empty values are removed. replace keeps typing out of history.
  const setFilters = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
    setParams(next, { replace: true });
    setVisible(PAGE_SIZE);
  };

  // Star clicks show right away (followChanges) while the request runs.
  const [followChanges, setFollowChanges] = useState({});
  const [followBusy, setFollowBusy] = useState({});

  const followedIds = useMemo(() => {
    const ids = new Set((followedTeams || []).map((t) => t._id || t.id));
    Object.entries(followChanges).forEach(([id, on]) => (on ? ids.add(id) : ids.delete(id)));
    return ids;
  }, [followedTeams, followChanges]);

  const toggleFollow = (program) => {
    const follow = !followedIds.has(program.id);
    setFollowChanges((c) => ({ ...c, [program.id]: follow }));
    setFollowBusy((b) => ({ ...b, [program.id]: true }));
    dispatch(follow ? followTeam(program.id) : unfollowTeam(program.id))
      .unwrap()
      .then(() => dispatch(fetchFollowedTeams({ page: 1, limit: 100 })))
      .catch(() => setFollowChanges((c) => ({ ...c, [program.id]: !follow })))
      .finally(() => setFollowBusy((b) => ({ ...b, [program.id]: false })));
  };

  const { programs, loading, error } = state;

  const counts = useMemo(() => ({
    total: programs.length,
    njcaa: programs.filter((p) => p.association === "NJCAA").length,
    cccaa: programs.filter((p) => p.association === "3C2A").length,
    states: new Set(programs.map((p) => p.state).filter(Boolean)).size,
  }), [programs]);

  const divisionOptions = association
    ? DIVISION_OPTIONS[association]
    : [
        ...DIVISION_OPTIONS.NJCAA.map((o) => ({ ...o, label: `NJCAA ${o.label}` })),
        ...DIVISION_OPTIONS["3C2A"].map((o) => ({ ...o, label: `3C2A ${o.label}` })),
      ];

  const regionOptions = useMemo(() => {
    const regions = [...new Set(programs.map((p) => p.region).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
    return regions.map((r) => ({ value: r, label: `Region ${r}` }));
  }, [programs]);

  const stateOptions = useMemo(() => {
    const scoped = association ? programs.filter((p) => p.association === association) : programs;
    const states = [...new Set(scoped.map((p) => p.state).filter(Boolean))].sort();
    return states.map((s) => ({ value: s, label: s }));
  }, [programs, association]);

  const results = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = programs.filter((p) =>
      (!association || p.association === association)
      && (!division || p.division === division)
      && (!region || p.region === region)
      && (!stateFilter || p.state === stateFilter)
      && (!followingOnly || followedIds.has(p.id))
      && words.every((w) => p.searchText.includes(w))
    );
    const byName = (a, b) => a.name.localeCompare(b.name);
    if (sort === "record") return list.sort((a, b) => b.winPct - a.winPct || byName(a, b));
    if (sort === "state") return list.sort((a, b) => (a.state || "~").localeCompare(b.state || "~") || byName(a, b));
    return list.sort(byName);
  }, [programs, query, association, division, region, stateFilter, followingOnly, followedIds, sort]);

  const activeFilters = FILTER_KEYS.some((key) => params.get(key));
  const clearFilters = () => setFilters(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));

  const changeAssociation = (value) => {
    const keepDivision = !value || DIVISION_OPTIONS[value].some((o) => o.value === division);
    setFilters({
      assoc: value,
      div: keepDivision ? division : "",
      region: value === "3C2A" ? "" : region,
      state: value === "3C2A" && stateFilter !== "California" ? "" : stateFilter,
    });
  };

  // Picking a division also picks its association so the toggle stays in sync.
  const changeDivision = (value) => {
    const assoc = DIVISION_OPTIONS.NJCAA.some((o) => o.value === value) ? "NJCAA" : value ? "3C2A" : association;
    setFilters({ div: value, assoc, region: assoc === "3C2A" ? "" : region });
  };

  const openProgram = (program) =>
    navigate(`${basePath}/team/${program.id}`, { state: { teamData: program.team } });

  return (
    <div className="space-y-6 text-left">
      {/* Navy hero with search */}
      <section className="relative overflow-hidden bg-jp-band rounded-3xl px-5 sm:px-8 py-7 sm:py-9">
        {/* Faint baseball diamond in the corner */}
        <svg className="pointer-events-none absolute -right-10 -bottom-16 w-72 h-72 text-white/[0.06]" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="6" aria-hidden="true">
          <path d="M100 20 L180 100 L100 180 L20 100 Z" />
          <circle cx="100" cy="100" r="10" fill="currentColor" />
          <rect x="94" y="14" width="12" height="12" fill="currentColor" transform="rotate(45 100 20)" />
          <rect x="174" y="94" width="12" height="12" fill="currentColor" transform="rotate(45 180 100)" />
          <rect x="14" y="94" width="12" height="12" fill="currentColor" transform="rotate(45 20 100)" />
        </svg>

        <div className="relative">
          <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-2">Team Directory</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-anton leading-[1.1] tracking-tight text-white uppercase">
            Find a Program
          </h1>
          <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-2xl">
            Browse every NJCAA and 3C2A baseball program. Search by school, city, or conference, then open a team for its roster, schedule, and stats.
          </p>

          <label className="relative block mt-6 max-w-2xl">
            <span className="sr-only">Search teams</span>
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setFilters({ q: e.target.value })}
              placeholder="Search teams, cities, or conferences"
              className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white text-slate-900 placeholder-slate-400 text-base shadow-lg focus:outline-none focus:ring-4 focus:ring-yellow-300/40"
            />
          </label>

          {!loading && !error && (
            <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              {[
                { label: "Programs", value: counts.total },
                { label: "NJCAA", value: counts.njcaa },
                { label: "3C2A", value: counts.cccaa },
                { label: "States", value: counts.states },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl bg-white/[0.07] border border-white/10 px-3 py-2">
                  <dt className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{stat.label}</dt>
                  <dd className="text-white text-xl font-extrabold tabular-nums">{stat.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </section>

      {/* Filters */}
      <section className={CARD}>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 items-start">
          <div>
            <FilterLabel>Association</FilterLabel>
            <SegmentedToggle options={ASSOCIATIONS} value={association} onChange={changeAssociation} ariaLabel="Association" />
          </div>
          <div>
            <FilterLabel>Division</FilterLabel>
            <SimpleDropdown
              variant="themed"
              value={division}
              onChange={changeDivision}
              placeholder="All divisions"
              options={[{ value: "", label: "All divisions" }, ...divisionOptions]}
            />
          </div>
          <div>
            <FilterLabel>Region</FilterLabel>
            <SimpleDropdown
              variant="themed"
              value={region}
              onChange={(value) => setFilters({ region: value })}
              placeholder={association === "3C2A" ? "NJCAA only" : "All regions"}
              disabled={association === "3C2A"}
              options={[{ value: "", label: "All regions" }, ...regionOptions]}
            />
          </div>
          <div>
            <FilterLabel>State</FilterLabel>
            <SimpleDropdown
              variant="themed"
              value={stateFilter}
              onChange={(value) => setFilters({ state: value })}
              placeholder="All states"
              options={[{ value: "", label: "All states" }, ...stateOptions]}
            />
          </div>
          <div>
            <FilterLabel>Sort by</FilterLabel>
            <SimpleDropdown
              variant="themed"
              value={sort === "name" ? "" : sort}
              onChange={(value) => setFilters({ sort: value === "name" ? "" : value })}
              placeholder="Name (A–Z)"
              options={SORTS}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-jp-outline/40 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-jp-muted">
            {loading ? "Loading teams…" : (
              <>
                <span className="text-jp-ink font-extrabold tabular-nums">{results.length}</span>{" "}
                {results.length === 1 ? "program" : "programs"}
                {activeFilters ? " match" : ""}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            {followedIds.size > 0 && (
              <button
                type="button"
                onClick={() => setFilters({ following: followingOnly ? "" : "1" })}
                aria-pressed={followingOnly}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  followingOnly
                    ? "border-jp-accent bg-jp-accent/15 text-jp-accent-ink"
                    : "border-jp-outline/70 text-jp-muted hover:text-jp-ink"
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                Teams I follow ({followedIds.size})
              </button>
            )}
            {activeFilters && (
              <button type="button" onClick={clearFilters} className="px-3 py-1.5 rounded-full text-xs font-bold text-jp-muted hover:text-jp-ink">
                Clear filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      {error ? (
        <div className={`${CARD} text-center py-10`}>
          <p className="text-jp-ink font-bold">{error}</p>
          <button type="button" onClick={load} className="mt-4 px-5 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold">
            Try again
          </button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 9 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : results.length === 0 ? (
        <div className={`${CARD} text-center py-12`}>
          <p className="text-lg font-bold text-jp-ink">No programs match</p>
          <p className="text-sm mt-2 text-jp-muted">Try a different search or fewer filters.</p>
          {activeFilters && (
            <button type="button" onClick={clearFilters} className="mt-4 px-5 py-2 rounded-lg border border-jp-outline text-jp-ink text-sm font-semibold hover:border-jp-accent">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {results.slice(0, visible).map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                following={followedIds.has(program.id)}
                followBusy={Boolean(followBusy[program.id])}
                onOpen={openProgram}
                onToggleFollow={toggleFollow}
              />
            ))}
          </div>
          {visible < results.length && (
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE * 2)}
                className="px-6 py-2.5 rounded-lg border border-jp-outline bg-jp-card text-jp-ink text-sm font-semibold hover:border-jp-accent transition-colors"
              >
                Show more teams
              </button>
              <p className="text-xs text-jp-muted">Showing {visible} of {results.length}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
